import { Component, forwardRef, HostBinding, Input } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { isEditableByMode } from '../utils/ontology-editing.util';
import { ModeView } from '../../../shared/ontology/model/mode-view.enum';

@Component({
    selector: 'jhi-textarea',
    template: `
		@if (isEditable()) {
			<textarea
				class="form-control"
				[attr.name]="name"
				[attr.id]="id || name"
				[attr.rows]="rows"
				[attr.placeholder]="placeholder || null"
				[required]="required"
				[disabled]="disabled || isDisabledByMode()"
				[value]="value ?? ''"
				(input)="onInternalChange($event)"
				(blur)="markTouched()"
			></textarea>
		} @else {
			<div class="form-control-plaintext">
				<truncate-span [textInput]=value?value:defaultValue></truncate-span>
			</div>
		}
    `,
    styles: [
        `
            :host.ng-invalid.ng-touched textarea.form-control {
                border-left: 6px solid var(--bs-danger);
                padding-left: calc(.75rem - 6px);
            }

            :host.ng-valid.ng-touched textarea.form-control {
                border-left: 6px solid var(--bs-success);
                padding-left: calc(.75rem - 6px);
            }

            /* In CREATE modeView, show invalid immediately (even before touch). */
            :host.modeView-create.ng-invalid textarea.form-control {
                border-left: 6px solid var(--bs-danger);
                padding-left: calc(.75rem - 6px);
            }

            /* In other modes, show invalid only after touch. */
            :host.ng-invalid.ng-touched textarea.form-control {
                border-left: 6px solid var(--bs-danger);
                padding-left: calc(.75rem - 6px);
            }
        `
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TextareaComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => TextareaComponent),
            multi: true
        }
    ]
})
export class TextareaComponent implements ControlValueAccessor, Validator {
    @Input() name!: string;
    @Input() id?: string;

    // ModeView replaces the old AngularJS `editable()` behavior.
    // Expected values: 'VIEW' | 'CREATE' | 'EDIT' | 'UPDATE' (case-insensitive).
    @Input() modeView: ModeView = ModeView.VIEW;

    // The full entity backing the textarea. Used to evaluate edit permissions (metadata).
    @Input() model: any;

    // Name of the field in the model (e.g., 'description').
    @Input() property?: string;

    // Extra guard: even if the mode allows editing, caller may force-disable.
    @Input() disabled = false;

    @Input() required = false;
    @Input() maxLength?: number;
    @Input() pattern?: string;

    @Input() rows = 3;
    @Input() placeholder?: string;
    @Input() defaultValue = '';

    value: string | null = null;

    // Add a host CSS class so CREATE modeView can trigger immediate invalid styling.
    @HostBinding('class.modeView-create')
    public get isCreateMode(): boolean {
        return this.modeView === ModeView.CREATE;
    }

    private onChange: (v: string | null) => void = () => {
    };
    private onTouched: () => void = () => {
    };

    writeValue(v: string | null): void {
        this.value = v;
    }

    registerOnChange(fn: (v: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
    // ModeView & input handlers
    public onInternalChange(event: Event): void {
        const target = event.target as HTMLTextAreaElement | null;
        if (!target) {
            return;
        }
        const newValue = target.value ?? '';
        this.value = newValue;
        this.onChange(newValue);
    }

    public markTouched(): void {
        this.onTouched();
    }

    public isEditable(): boolean {
        return isEditableByMode({
            modeView: this.modeView,
            model: this.model,
            property: this.property ?? ''
        });
    }

    public isDisabledByMode(): boolean {
        // Caller-driven disabled takes precedence.
        if (this.disabled) {
            return true;
        }
        if (this.modeView === ModeView.VIEW) {
            return true;
        }
        // For EDIT/CREATE, disable if not editable by metadata.
        if ((this.modeView === ModeView.EDIT || this.modeView === ModeView.CREATE) && !this.isEditable()) {
            return true;
        }
        return false;
    }

    validate(control: AbstractControl<any, any>): ValidationErrors {
        if (this.modeView === ModeView.VIEW) {
            return null;
        }

        const current = (this.value ?? '').toString();
        if (this.required === true && current.trim().length === 0) {
            return { required: true };
        }

        if (typeof this.maxLength === 'number' &&
            this.maxLength >= 0 &&
            current.length > this.maxLength
        ) {
            return { maxlength: true };
        }

        if (this.pattern) {
            const re = new RegExp(this.pattern);
            if (current && !re.test(current)) {
                return { pattern: true };
            }
        }
        return null;
    }

}
